import React, { useEffect, useRef } from 'react';

const ResultsVisualization = ({ profile, onFindMatches, onRetakeAssessment }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (canvasRef.current && profile?.bigFive) {
      drawRadarChart();
    }
  }, [profile]);

  const drawRadarChart = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 40;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Traits data
    const traits = [
      { name: 'Openness', value: profile.bigFive.openness },
      { name: 'Conscientiousness', value: profile.bigFive.conscientiousness },
      { name: 'Extraversion', value: profile.bigFive.extraversion },
      { name: 'Agreeableness', value: profile.bigFive.agreeableness },
      { name: 'Neuroticism', value: profile.bigFive.neuroticism }
    ];
    
    const numTraits = traits.length;
    const angleSlice = (Math.PI * 2) / numTraits;
    
    // Draw grid circles
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 5; i++) {
      ctx.beginPath();
      for (let j = 0; j <= numTraits; j++) {
        const angle = j * angleSlice - Math.PI / 2;
        const x = centerX + Math.cos(angle) * (radius * i / 5);
        const y = centerY + Math.sin(angle) * (radius * i / 5);
        if (j === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    }
    
    // Draw axis lines
    for (let i = 0; i < numTraits; i++) {
      const angle = i * angleSlice - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX + Math.cos(angle) * radius,
        centerY + Math.sin(angle) * radius
      );
      ctx.stroke();
    }
    
    // Draw data polygon
    ctx.beginPath();
    ctx.fillStyle = 'rgba(59, 130, 246, 0.3)';
    ctx.strokeStyle = 'rgb(59, 130, 246)';
    ctx.lineWidth = 2;
    
    traits.forEach((trait, i) => {
      const angle = i * angleSlice - Math.PI / 2;
      const value = trait.value / 100;
      const x = centerX + Math.cos(angle) * radius * value;
      const y = centerY + Math.sin(angle) * radius * value;
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    
    // Draw labels
    ctx.fillStyle = 'white';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    traits.forEach((trait, i) => {
      const angle = i * angleSlice - Math.PI / 2;
      const labelRadius = radius + 25;
      const x = centerX + Math.cos(angle) * labelRadius;
      const y = centerY + Math.sin(angle) * labelRadius;
      
      ctx.fillText(trait.name, x, y);
      
      // Draw value
      ctx.font = '10px sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.fillText(`${trait.value}%`, x, y + 12);
      ctx.fillStyle = 'white';
      ctx.font = '12px sans-serif';
    });
  };

  const getPersonalityDescription = () => {
    if (!profile?.bigFive) return '';
    
    const { openness, conscientiousness, extraversion, agreeableness, neuroticism } = profile.bigFive;
    
    let description = "You are ";
    
    // Extraversion
    if (extraversion > 70) {
      description += "highly outgoing and energetic, ";
    } else if (extraversion < 30) {
      description += "introspective and reserved, ";
    } else {
      description += "balanced between social and solitary activities, ";
    }
    
    // Openness
    if (openness > 70) {
      description += "creative and open to new experiences, ";
    } else if (openness < 30) {
      description += "practical and traditional, ";
    }
    
    // Conscientiousness
    if (conscientiousness > 70) {
      description += "organized and goal-oriented, ";
    } else if (conscientiousness < 30) {
      description += "flexible and spontaneous, ";
    }
    
    // Agreeableness
    if (agreeableness > 70) {
      description += "compassionate and cooperative, ";
    } else if (agreeableness < 30) {
      description += "competitive and assertive, ";
    }
    
    // Neuroticism
    if (neuroticism < 30) {
      description += "and emotionally stable.";
    } else if (neuroticism > 70) {
      description += "and emotionally sensitive.";
    } else {
      description += "with moderate emotional sensitivity.";
    }
    
    return description;
  };

  const getCommunicationAdvice = () => {
    const style = profile?.communicationStyle;
    
    const advice = {
      intellectual: "You thrive in deep, meaningful conversations. Lead with thought-provoking questions and share your insights.",
      direct: "Your straightforward approach is refreshing. Keep conversations clear and purposeful.",
      storytelling: "You connect through narratives. Share experiences and encourage others to do the same.",
      humor_based: "Your wit is your strength. Use humor to break the ice and keep conversations light.",
      casual: "You excel at making others comfortable. Keep things relaxed and go with the flow."
    };
    
    return advice[style] || advice.casual;
  };

  return (
    <div className="results-visualization">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          Your Personality Profile
        </h2>
        <p className="text-gray-400">
          Understanding your social communication style
        </p>
      </div>
      
      {/* Radar Chart */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <canvas 
          ref={canvasRef}
          width={400}
          height={400}
          className="w-full max-w-md mx-auto"
        />
      </div>
      
      {/* Personality Summary */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-3">
          Personality Summary
        </h3>
        <p className="text-gray-300 mb-4">
          {getPersonalityDescription()}
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-400">Communication Style</span>
            <p className="text-white font-semibold capitalize">
              {profile?.communicationStyle?.replace('_', ' ')}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-400">Social Confidence</span>
            <p className="text-white font-semibold">
              {profile?.socialConfidence || 0}%
            </p>
          </div>
        </div>
      </div>
      
      {/* Communication Advice */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-3">
          💡 Communication Tips
        </h3>
        <p className="text-gray-300">
          {getCommunicationAdvice()}
        </p>
      </div>
      
      {/* Social Metrics */}
      {profile?.socialMetrics && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">
            Social Metrics
          </h3>
          <div className="space-y-3">
            {Object.entries(profile.socialMetrics).map(([key, value]) => (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400 capitalize">
                    {key.replace(/_/g, ' ')}
                  </span>
                  <span className="text-gray-300">{value}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full"
                    style={{ width: `${value}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onFindMatches}
          className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Find Compatible Matches
        </button>
        <button
          onClick={onRetakeAssessment}
          className="flex-1 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
        >
          Retake Assessment
        </button>
      </div>
    </div>
  );
};

export default ResultsVisualization;